// Site-wide auth state.
//
// Auth is now carried in an **HttpOnly session cookie** (`en_session`) set
// by the backend on login/register. JavaScript on the page cannot read it,
// which kills the XSS-token-theft attack surface. We keep the *user object*
// in memory only — on a fresh page load we ask `/auth/me` to tell us who
// the browser is authenticated as.
//
// Legacy `localStorage["en_token"]` values are aggressively wiped on load
// so a compromised script can't lift a leftover token from a past session.

import { useCallback, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const TOKEN_KEY = "en_token";

// One-time cleanup: remove any legacy tokens still hanging around from the
// old localStorage-based auth. Runs at module load.
if (typeof window !== "undefined") {
    try {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem("en_admin_token");
        localStorage.removeItem("auth_token");
        localStorage.removeItem("token");
    } catch {
        // Some browsers can throw in private mode; safe to ignore.
    }
}

// Every axios request now includes the session cookie automatically.
axios.defaults.withCredentials = true;

const listeners = new Set();
let cachedUser = null;
// A dummy sentinel that changes every time we (re)validate the session — we
// still expose a "token" in the returned state so existing consumers that
// destructure `{ token }` don't need to change shape immediately. It's not
// a JWT; it's just a truthy/falsy marker for "am I logged in".
let cachedSessionToken = null;

const notify = () =>
    listeners.forEach((fn) => fn({ user: cachedUser, token: cachedSessionToken }));

// Back-compat shim: old code called `authHeaders()` to grab
// `{Authorization: Bearer …}`. Now the cookie carries auth, so we return an
// empty object. Every existing call site keeps working without edits.
export const authHeaders = () => ({});

let bootstrapPromise = null;
async function bootstrap() {
    if (bootstrapPromise) return bootstrapPromise;
    bootstrapPromise = (async () => {
        try {
            const res = await axios.get(`${API}/auth/me`);
            cachedUser = res.data;
            cachedSessionToken = "cookie";
        } catch {
            cachedUser = null;
            cachedSessionToken = null;
        }
        notify();
    })();
    return bootstrapPromise;
}

// Kick off the bootstrap once, immediately, so the first component that
// renders sees a fresh answer.
if (typeof window !== "undefined") bootstrap();

export function useAuth() {
    const [state, setState] = useState({
        user: cachedUser,
        token: cachedSessionToken,
        loading: cachedUser === null && cachedSessionToken === null,
    });

    useEffect(() => {
        const fn = (s) => setState((prev) => ({ ...prev, ...s, loading: false }));
        listeners.add(fn);
        return () => listeners.delete(fn);
    }, []);

    // The bootstrap promise resolves once — hook into it so the loading
    // flag flips off after the first `/auth/me` roundtrip.
    useEffect(() => {
        bootstrap().then(() =>
            setState((prev) => ({
                ...prev,
                user: cachedUser,
                token: cachedSessionToken,
                loading: false,
            })),
        );
    }, []);

    const setSession = useCallback((_token, user) => {
        // `_token` is ignored — the HttpOnly cookie is the source of truth.
        // We only cache the user profile in memory.
        cachedUser = user || null;
        cachedSessionToken = user ? "cookie" : null;
        notify();
    }, []);

    const logout = useCallback(async () => {
        try {
            await axios.post(`${API}/auth/logout`);
        } catch {
            // Even if the request fails, purge local state — the browser
            // may still have the cookie but the user's intent is clear.
        }
        cachedUser = null;
        cachedSessionToken = null;
        notify();
    }, []);

    return { ...state, setSession, logout };
}
